import type { ParamsConfig } from './types'

type UnionToIntersection<U> = (U extends any ? (k: U) => void : never) extends (
  k: infer I,
) => void
  ? I
  : never

/**
 * Merges multiple configs into one.
 *
 * Note: Merging enum params is not supported because it will break the derived
 * params type. At the moment, mergeConfigs will throw when it encounters
 * duplicated enums.
 */
export function mergeConfigs<T extends ParamsConfig[]>(...configs: T) {
  const stringParams = new Set<string>()
  const boolParams = new Set<string>()
  const numberParams = new Set<string>()
  const enumParams: Record<string, readonly string[]> = {}

  for (const config of configs) {
    for (const param of config.stringParams) {
      stringParams.add(param)
    }
    for (const param of config.boolParams) {
      boolParams.add(param)
    }
    for (const param of config.numberParams) {
      numberParams.add(param)
    }
    for (const param of Object.keys(config.enumParams)) {
      if (enumParams[param]) {
        throw new Error(
          `Enum param ${param} is defined multiple times. Overriding enum declaration is not supported.`,
        )
      }
      enumParams[param] = config.enumParams[param]
    }
  }

  return {
    stringParams: Array.from(stringParams),
    boolParams: Array.from(boolParams),
    numberParams: Array.from(numberParams),
    enumParams,
  } as {
    stringParams: [T[number]['stringParams'][number]]
    boolParams: [T[number]['boolParams'][number]]
    numberParams: [T[number]['numberParams'][number]]
    enumParams: UnionToIntersection<T[number]['enumParams']>
  }
}
